/** Thursday 15:00 org-local default until ops saves a cutoff. Ops closes the week; not a cron. */

export const DEFAULT_LABOR_PAY_CUTOFF_WEEKDAY = 4; // Thursday (JS getDay)
export const DEFAULT_LABOR_PAY_CUTOFF_HOUR = 15;
export const DEFAULT_LABOR_PAY_TIMEZONE = "America/New_York";

export const LABOR_PAY_WEEKDAY_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export type LaborPayCutoffConfig = {
  weekday: number;
  hour: number;
  minute: number;
  timeZone: string;
};

export const DEFAULT_LABOR_PAY_CUTOFF: LaborPayCutoffConfig = {
  weekday: DEFAULT_LABOR_PAY_CUTOFF_WEEKDAY,
  hour: DEFAULT_LABOR_PAY_CUTOFF_HOUR,
  minute: 0,
  timeZone: DEFAULT_LABOR_PAY_TIMEZONE,
};

export function parseLaborPayCutoffConfig(raw: unknown): LaborPayCutoffConfig {
  const o = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const weekday = Number(o.weekday);
  const hour = Number(o.hour);
  const minute = Number(o.minute);
  const timeZone = typeof o.timeZone === "string" ? o.timeZone.trim() : "";
  return {
    weekday:
      Number.isInteger(weekday) && weekday >= 0 && weekday <= 6
        ? weekday
        : DEFAULT_LABOR_PAY_CUTOFF_WEEKDAY,
    hour: Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : DEFAULT_LABOR_PAY_CUTOFF_HOUR,
    minute: Number.isInteger(minute) && minute >= 0 && minute <= 59 ? minute : 0,
    timeZone: timeZone || DEFAULT_LABOR_PAY_TIMEZONE,
  };
}

function ymdInZone(d: Date, timeZone: string): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const pick = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? "0");
  return { year: pick("year"), month: pick("month"), day: pick("day") };
}

function dowInZone(d: Date, timeZone: string): number {
  const name = new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short" }).format(d);
  const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[name] ?? d.getUTCDay();
}

/** Instant when `timeZone` wall clock is year-month-day hour:minute. */
export function zonedLocalDate(
  year: number,
  month: number,
  day: number,
  hour: number,
  timeZone: string,
  minute = 0
): Date {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  let ms = Date.UTC(year, month - 1, day, hour, minute, 0);
  for (let i = 0; i < 8; i += 1) {
    const p = dtf.formatToParts(new Date(ms));
    const n = (t: string) => Number(p.find((x) => x.type === t)?.value ?? "0");
    const delta =
      Date.UTC(year, month - 1, day, hour, minute) -
      Date.UTC(n("year"), n("month") - 1, n("day"), n("hour"), n("minute"));
    if (delta === 0) return new Date(ms);
    ms += delta;
  }
  return new Date(ms);
}

export function laborPayCutoffAt(
  now: Date,
  opts?: { weekday?: number; hour?: number; minute?: number; timeZone?: string }
): Date {
  const weekday = opts?.weekday ?? DEFAULT_LABOR_PAY_CUTOFF_WEEKDAY;
  const hour = opts?.hour ?? DEFAULT_LABOR_PAY_CUTOFF_HOUR;
  const minute = opts?.minute ?? 0;
  const timeZone = opts?.timeZone ?? DEFAULT_LABOR_PAY_TIMEZONE;
  const ymd = ymdInZone(now, timeZone);
  const dow = dowInZone(now, timeZone);
  const daysTo = (weekday - dow + 7) % 7;
  const startNoon = zonedLocalDate(ymd.year, ymd.month, ymd.day, 12, timeZone);
  const target = new Date(startNoon.getTime() + daysTo * 24 * 60 * 60 * 1000);
  const t = ymdInZone(target, timeZone);
  return zonedLocalDate(t.year, t.month, t.day, hour, timeZone, minute);
}

export function weekEndingDate(cutoff: Date, timeZone = DEFAULT_LABOR_PAY_TIMEZONE): string {
  const ymd = ymdInZone(cutoff, timeZone);
  return `${ymd.year}-${String(ymd.month).padStart(2, "0")}-${String(ymd.day).padStart(2, "0")}`;
}

export function isRequestedBeforeCutoff(
  paymentRequestedAt: string | null,
  cutoff: Date
): boolean {
  if (!paymentRequestedAt) return false;
  return new Date(paymentRequestedAt).getTime() <= cutoff.getTime();
}

export function laborPayCutoffHhmm(config: LaborPayCutoffConfig): string {
  return `${String(config.hour).padStart(2, "0")}:${String(config.minute).padStart(2, "0")}`;
}

export function laborPayCutoffFromHhmm(
  hhmm: string,
  weekday: number,
  timeZone = DEFAULT_LABOR_PAY_TIMEZONE
): LaborPayCutoffConfig {
  const [hRaw, mRaw] = hhmm.split(":");
  return parseLaborPayCutoffConfig({
    weekday,
    hour: Number(hRaw),
    minute: Number(mRaw),
    timeZone,
  });
}
